import Alert from '@mui/material/Alert';
import Box, { type BoxProps } from '@mui/material/Box';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { Table, TableName } from '@openx/components-table/Table.tsx';
import { type ListLalSegment, type ListSegment, lalSegmentStatus } from '@openx/types/segments.ts';
import { type AllTableCriteria, CriteriaDimension, SortDirection } from '@openx/types/tableCriteria.ts';
import { clientSideCriteriaProcessing } from '@openx/utils/lib/clientSideCriteriaProcessing.ts';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria.ts';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ConfirmationTableRow, LalConfirmationTableRowWithAudiences } from '../../../types.ts';
import { useSegmentContext } from '../../utils/context/SegmentContext.tsx';
import { ExpandableTableRow } from './components/ExpandableTableRow.tsx';
import { confirmationDialogTableColumns } from './tableColumns.tsx';

const BoldTypography = (props: TypographyProps) => <Typography component="span" variant="body1Bold" {...props} />;

const SegmentsTableBox = (props: BoxProps) => (
  <Box
    {...props}
    sx={{
      maxHeight: 344,
      overflowY: 'scroll',
      ...(props.sx ?? {}),
    }}
  />
);

const WarningAlertBox = (props: BoxProps) => (
  <Box
    data-test="confirmation-dialog-alert"
    {...props}
    sx={[
      {
        bgcolor: 'warning.light',
      },
      theme => ({
        margin: theme.spacing(2),
        marginBottom: theme.spacing(2.5),
        marginTop: theme.spacing(3),
        padding: theme.spacing(1.5, 2),
      }),
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

const AlertText = () => (
  <>
    The <BoldTypography>Segments</BoldTypography> you're trying to archive are currently linked to{' '}
    <BoldTypography>Audiences</BoldTypography>.<br />
    Please remove these associations <BoldTypography>before</BoldTypography> proceeding with the archive.
  </>
);

const LALProcessingAlertText = () => (
  <>
    The <BoldTypography>Segments</BoldTypography> you're trying to archive are currently in{' '}
    <BoldTypography>processing</BoldTypography>.
    <br />
    Please wait until processing is complete before proceeding with the archive.
  </>
);

const defaultCriteria: AllTableCriteria = {
  [CriteriaDimension.SORT]: {
    column: 'name',
    direction: SortDirection.ASC,
    sortableColumns: ['name', 'linked_audiences', 'updated_date'],
  },
};

type Props = {
  data: ListSegment[] | ListLalSegment[];
  setConfirmationDisabled?: (disabled: boolean) => void;
  isLalSegments?: boolean;
};

export const ArchiveSegmentDialogContent = ({ data, setConfirmationDisabled, isLalSegments = false }: Props) => {
  const { useSegmentsLinkedAudiences, useLalSegmentsWithDetails } = useSegmentContext();

  const [criteria, setCriteria] = useState<AllTableCriteria>(defaultCriteria);

  const segmentIds = useMemo(() => data.map(segment => segment.id), [data]);
  const fetchSegmentsFunc = useMemo(
    () => (isLalSegments ? useLalSegmentsWithDetails : useSegmentsLinkedAudiences),
    [isLalSegments, useLalSegmentsWithDetails, useSegmentsLinkedAudiences]
  );

  const fetchedSegments = fetchSegmentsFunc({ segmentIds });
  const segmentsLoading = fetchedSegments.loading;

  const segments: ConfirmationTableRow[] | LalConfirmationTableRowWithAudiences[] = useMemo(() => {
    if ('lalSegments' in fetchedSegments) {
      // Map LAL segments to include linked audiences (count) and segment audiences
      return fetchedSegments.lalSegments.map(segment => ({
        ...segment,
        linked_audiences:
          segment.lookalike_segment_similarities
            ?.filter(_seg => _seg.segment.segment_audiences)
            .flatMap(_seg => _seg.segment.segment_audiences)?.length || 0,
        segment_audiences:
          segment.lookalike_segment_similarities
            ?.filter(_seg => _seg.segment.segment_audiences)
            .flatMap(_seg => _seg.segment.segment_audiences) || [],
      }));
    } else if ('segments' in fetchedSegments) {
      // Map the segments to include linked audiences (count)
      return fetchedSegments.segments.map(segment => ({
        ...segment,
        linked_audiences: segment.segment_audiences.length,
      }));
    }
    // Fallback to empty array if no segments are fetched yet
    return [];
  }, [fetchedSegments]);

  const handleCriteriaChange = useCallback(
    newCriteria => {
      if (
        newCriteria?.dimension === 'sort' &&
        newCriteria.value?.column === criteria.sort?.column &&
        newCriteria.value?.direction === 'asc' &&
        newCriteria.value?.column !== defaultCriteria.sort?.column
      ) {
        newCriteria.value.column = defaultCriteria.sort?.column;
        newCriteria.value.direction = defaultCriteria.sort?.direction;
      }

      const newCriteriaProcessed = applyCriteriaChange(criteria, newCriteria);

      if (!isEqual({ ...newCriteriaProcessed }, { ...criteria })) {
        setCriteria(newCriteriaProcessed);
      }
    },
    [criteria]
  );

  const hasAssociatedAudiences = useMemo(() => segments.some(segment => segment.segment_audiences.length), [segments]);
  const hasLalSegmentsInProcessing = useMemo(
    () =>
      isLalSegments &&
      segments.some((segment: LalConfirmationTableRowWithAudiences) => segment.status === lalSegmentStatus.PROCESSING),
    [segments, isLalSegments]
  );

  const sortedSegments = clientSideCriteriaProcessing(segments, criteria);

  useEffect(() => {
    setConfirmationDisabled?.(hasAssociatedAudiences || hasLalSegmentsInProcessing);
  }, [setConfirmationDisabled, hasAssociatedAudiences, hasLalSegmentsInProcessing]);

  return (
    <Box
      data-test="confirmation-dialog-content"
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      {hasAssociatedAudiences && (
        <WarningAlertBox>
          <Alert data-test="alert-message" severity="warning" sx={{ padding: 0 }}>
            <AlertText />
          </Alert>
        </WarningAlertBox>
      )}

      {hasLalSegmentsInProcessing && (
        <WarningAlertBox>
          <Alert data-test="lal-processing-alert" severity="warning" sx={{ padding: 0 }}>
            <LALProcessingAlertText />
          </Alert>
        </WarningAlertBox>
      )}

      <SegmentsTableBox data-test="confirmation-dialog-table">
        <Table
          name={TableName.ARCHIVE_SEGMENTS}
          columns={confirmationDialogTableColumns}
          data={sortedSegments}
          enableTableHeaderCustomization={false}
          renderCustomRow={ExpandableTableRow}
          criteria={criteria}
          onCriteriaChange={handleCriteriaChange}
          loading={segmentsLoading}
          overrideTableCellStyles={{
            '&.MuiTableCell-body': {
              paddingLeft: 0,
            },
            '&.MuiTableCell-head': {
              '&:first-of-type': {
                paddingLeft: 5.75,
              },
              paddingBottom: 1,
              paddingLeft: 0,

              paddingTop: 1.25,
            },

            height: 'auto',
          }}
        />
      </SegmentsTableBox>
    </Box>
  );
};
